/**
 * Turns the AI's numbers into teaching. Nothing here invents a judgement: every
 * sentence is backed by a number from shared/ai.ts or a rule from the master's
 * list. Shape per docs/COACH-SPEC.md: predict, then reveal; silent on good
 * plays; rule + reason in card counts + exception; one principle at a time.
 */

import { type Card, label, rankOf, valueOf, RANK_NAME } from './cards.js';
import {
  type DiscardOption,
  type PegContext,
  type PegNoteKey,
  type PegOption,
  type Position,
  evaluateDiscards,
  evaluatePegs,
  mode,
  parDelta,
} from './ai.js';
import { type Score, itemLabel } from './scoring.js';

// ── principles (cribbage master, Gate A) ────────────────────────────────────

export const TIPS = {
  leadFive: 'Never lead a 5: up to sixteen of the 45 cards you can\'t see are ten-cards, and every one makes fifteen for two.',
  leadFour: 'Lead a 4: it is the highest card no single card can make fifteen on, so the dealer scores only by pairing it — three cards.',
  leadFromPair: 'Lead from a pair: if the dealer pairs you (about one time in eight), your third makes pair royal for six. Not with 5s.',
  twentyOne: 'Never bring the count to 21: sixteen ten-cards make 31 for two. Same for 5 — a ten-card makes fifteen.',
  lowCard: 'Hold an ace or 2 for last: it fits under almost any count, so it takes the go or 31 instead of giving it away.',
  magicEleven: 'Two cards totalling eleven (4-7, 5-6, 3-8, 2-9, A-10) beat a ten-card lead: play one, and if another ten-card follows, the second makes 31.',
  nineteen: 'A hand with no points is a "nineteen hand" — 19 is the lowest score no hand can make. Say nineteen and move on.',
  poneFirst: 'At the show the non-dealer counts first, and the game ends the instant anyone reaches 121 — pone\'s hand wins the race before the crib is turned.',
  par: 'Par is 26 every two deals, about 16 as dealer and 10 as pone. Ahead, play off: deny the crib, no gambles. Behind, play on: safe play loses a race you\'re losing.',
  cribOwner: 'Count what the crib is worth to the dealer before you keep points. As pone, a 5 or a pair in the crib is a gift; as dealer, it is yours.',
  counting: 'Count in order, every time: fifteens, pairs, runs, flush, nobs. The order is why experienced players never miss a combination.',
} as const;

export function parPhrase(pos: Position): string {
  const d = Math.round(parDelta(pos));
  const m = mode(pos);
  if (m === 'neutral') return 'You are about on par — play straight.';
  return `You are ${Math.abs(d)} ${d > 0 ? 'ahead of' : 'behind'} par — play ${m}.`;
}

// ── discard ─────────────────────────────────────────────────────────────────

export type Grade = 'good' | 'fine' | 'miss';

export interface DiscardReview {
  options: DiscardOption[];
  chosen: DiscardOption;
  best: DiscardOption;
  rank: number;
  gap: number;
  grade: Grade;
  headline: string;
  explanation: string;
  principle: string;
}

export function gradeGap(gap: number): Grade {
  return gap <= 0.3 ? 'good' : gap <= 1.0 ? 'fine' : 'miss';
}

export function reviewDiscard(hand: readonly Card[], thrown: readonly Card[], pos: Position): DiscardReview {
  const options = evaluateDiscards(hand, pos);
  const key = (t: readonly Card[]) => [...t].sort((a, b) => a - b).join(',');
  const idx = options.findIndex((o) => key(o.throw) === key(thrown));
  const chosen = options[idx]!;
  const best = options[0]!;
  const gap = best.total - chosen.total;
  const grade = gradeGap(gap);
  const ordinal = (n: number) => (n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`);
  const headline =
    idx === 0
      ? `Best keep of 15.`
      : `Ranked ${ordinal(idx + 1)} of 15 · ${gap.toFixed(1)} pt behind ${cards(best.keep)} / ${cards(best.throw)}.`;
  return { options, chosen, best, rank: idx + 1, gap, grade, headline, explanation: explainDiscard(chosen, best, pos), principle: discardPrinciple(chosen, best, pos) };
}

function cards(cs: readonly Card[]): string {
  return cs.map(label).join(' ');
}

function explainDiscard(chosen: DiscardOption, best: DiscardOption, pos: Position): string {
  const who = pos.isDealer ? 'your crib' : 'their crib';
  const f = (n: number) => n.toFixed(1);
  if (chosen === best) {
    return (
      `${cards(chosen.keep)} averages ${f(chosen.handEV)} over the 46 possible starters` +
      (chosen.baseHand > 0 ? ` (${chosen.baseHand} in hand before the cut)` : '') +
      `, and ${cards(chosen.throw)} is worth about ${f(chosen.cribEV)} in ${who}.`
    );
  }
  // Everything below is said from the player's side: "your throw", "your hand".
  const handGap = best.handEV - chosen.handEV; // > 0: the best keep is better in hand
  // > 0: the player's throw helped them more than the best throw would have
  // (dealer: more in own crib; pone: less given to the dealer's crib).
  const cribHelp = pos.isDealer ? chosen.cribEV - best.cribEV : best.cribEV - chosen.cribEV;
  const parts: string[] = [];
  if (Math.abs(handGap) >= Math.abs(cribHelp)) {
    parts.push(`${cards(chosen.keep)} averages ${f(chosen.handEV)} in hand; ${cards(best.keep)} averages ${f(best.handEV)}.`);
    if (Math.abs(cribHelp) > 0.3) {
      if (cribHelp > 0) parts.push(pos.isDealer ? `Your throw was worth ${f(cribHelp)} more in your crib, but not enough to make up the hand.` : `Your throw did deny the dealer ${f(cribHelp)}, but not enough to make up the hand.`);
      else parts.push(pos.isDealer ? `And ${cards(best.throw)} would have been worth ${f(-cribHelp)} more in your crib.` : `And your throw gave the dealer's crib about ${f(-cribHelp)} more.`);
    }
  } else {
    parts.push(
      pos.isDealer
        ? `${cards(chosen.throw)} is worth ${f(chosen.cribEV)} in your crib; ${cards(best.throw)} is worth ${f(best.cribEV)}.`
        : `${cards(chosen.throw)} gives the dealer's crib about ${f(chosen.cribEV)}; ${cards(best.throw)} gives only ${f(best.cribEV)}.`,
    );
    if (Math.abs(handGap) > 0.3) {
      if (handGap < 0) parts.push(`Your hand was ${f(-handGap)} better for it, but not enough.`);
      else parts.push(`Your hand was ${f(handGap)} worse as well.`);
    }
  }
  return parts.join(' ');
}

function discardPrinciple(chosen: DiscardOption, best: DiscardOption, pos: Position): string {
  // The best play gets the general principle, never a scolding.
  if (chosen === best) return mode(pos) === 'neutral' ? TIPS.cribOwner : TIPS.par;
  const thrownRanks = chosen.throw.map(rankOf);
  const bestRanks = best.throw.map(rankOf);
  const isPair = (r: number[]) => r[0] === r[1];
  const touching = (r: number[]) => Math.abs(r[0]! - r[1]!) === 1;
  if (!pos.isDealer && thrownRanks.includes(5) && !bestRanks.includes(5)) return TIPS.cribOwner;
  if (!pos.isDealer && isPair(thrownRanks) && !isPair(bestRanks)) return 'As pone, never throw a pair: it is two points in the dealer\'s crib before the cut.';
  if (!pos.isDealer && touching(thrownRanks) && !touching(bestRanks)) return 'As pone, touching cards (like 7-8) are run and fifteen material for the dealer\'s crib. Throw wide apart: 9-K, 6-K, 7-K.';
  if (pos.isDealer && bestRanks.includes(5) && !thrownRanks.includes(5)) return 'As dealer, a 5 belongs in your crib unless it is making fifteens in your hand: sixteen ten-cards make fifteen with it.';
  if (chosen.baseHand === 0 && best.baseHand > 0) return 'Points in hand before the cut are certain; points that need a starter are a wish. Prefer the sure ones when the gap is small.';
  return mode(pos) === 'neutral' ? TIPS.cribOwner : TIPS.par;
}

/** Graded hints before committing a discard. */
export function discardHint(hand: readonly Card[], pos: Position, level: 1 | 2 | 3): string {
  const best = evaluateDiscards(hand, pos)[0]!;
  if (level === 1) return pos.isDealer ? 'Your crib: what two cards would score together? Then check what your four still make.' : TIPS.cribOwner;
  if (level === 2) {
    const kept = best.keep;
    const ranks = [...new Set(kept.map(rankOf))].sort((a, b) => a - b);
    const run = ranks.length >= 3 && ranks.some((r, i) => i >= 2 && ranks[i - 1] === r - 1 && ranks[i - 2] === r - 2);
    const pair = kept.some((c, i) => kept.some((d, j) => j !== i && rankOf(c) === rankOf(d)));
    if (run) return 'Keep the run together; the two cards outside it are the ones to consider.';
    if (pair) return 'Keep the pair; look at which two of the rest add least.';
    return `Think about which four make the most fifteens together.`;
  }
  return `Keep ${cards(best.keep)}, throw ${cards(best.throw)}.`;
}

// ── pegging ─────────────────────────────────────────────────────────────────

export interface PegReview {
  options: PegOption[];
  chosen: PegOption;
  best: PegOption;
  loss: number;
  /** Text to show, or null if the play was fine (silence is the default). */
  line: string | null;
  principle: string | null;
}

const NOTE_TIP: Record<PegNoteKey, string> = {
  'lead-five': TIPS.leadFive,
  'lead-four': TIPS.leadFour,
  'lead-from-pair': TIPS.leadFromPair,
  'leaves-twenty-one': TIPS.twentyOne,
  'leaves-five': TIPS.twentyOne,
  'keeps-low-card': TIPS.lowCard,
  'magic-eleven': TIPS.magicEleven,
  'safe-dump': TIPS.par,
};

export function reviewPeg(ctx: PegContext, played: Card, threshold: number): PegReview {
  const options = evaluatePegs(ctx);
  const chosen = options.find((o) => o.card === played) ?? options[0]!;
  const best = options[0]!;
  const loss = best.total - chosen.total;
  if (loss < threshold || chosen === best) return { options, chosen, best, loss, line: null, principle: null };
  return { options, chosen, best, loss, line: explainPeg(chosen, best), principle: pegPrinciple(chosen, best) };
}

function explainPeg(chosen: PegOption, best: PegOption): string {
  const parts: string[] = [];
  if (best.immediate > chosen.immediate) {
    parts.push(`${label(best.card)} scored ${best.immediate} now (${best.items.map(itemLabel).join(', ').toLowerCase()}).`);
  }
  if (chosen.oppReply - best.oppReply > 0.2) {
    parts.push(`At ${chosen.countAfter} the opponent averages ${chosen.oppReply.toFixed(1)} in reply; at ${best.countAfter} only ${best.oppReply.toFixed(1)}.`);
  }
  if (best.myReply - chosen.myReply > 0.3) {
    parts.push(`${label(best.card)} left you ${best.myReply.toFixed(1)} of follow-up points on average.`);
  }
  const bad = chosen.notes.find((n) => n.delta < 0);
  if (bad) parts.unshift(NOTE_TIP[bad.key]);
  if (parts.length === 0) parts.push(`${label(best.card)} was worth about ${(best.total - chosen.total).toFixed(1)} more here.`);
  return parts.slice(0, 2).join(' ');
}

function pegPrinciple(chosen: PegOption, best: PegOption): string | null {
  const bad = chosen.notes.find((n) => n.delta < 0);
  if (bad) return NOTE_TIP[bad.key];
  const good = best.notes.find((n) => n.delta > 0);
  if (good) return NOTE_TIP[good.key];
  return null;
}

/** Graded hints before playing a card. */
export function pegHint(ctx: PegContext, level: 1 | 2 | 3): string {
  const options = evaluatePegs(ctx);
  const best = options[0]!;
  if (level === 1) {
    if (ctx.stack.length === 0) return 'Leading: a card under 5 cannot be made into fifteen; a card from a pair invites a pair you can answer.';
    return 'Score what you can now; otherwise leave a count the opponent cannot make fifteen or thirty-one on.';
  }
  if (level === 2) {
    if (best.immediate > 0) return `One card scores right now.`;
    return `Compare what the count would be after each card — one leaves the fewest scoring replies.`;
  }
  return `Play ${label(best.card)}.`;
}

// ── counting ────────────────────────────────────────────────────────────────

export interface CountReview {
  entered: number;
  truth: number;
  verdict: 'right' | 'over' | 'under';
  line: string;
  /** Items the player missed (undercount), for highlighting. */
  missed: Score['items'];
}

export function reviewCount(entered: number, score: Score): CountReview {
  const truth = score.total;
  if (entered === truth) return { entered, truth, verdict: 'right', line: truth === 0 ? 'No points — a nineteen hand. Right.' : `${truth}. Right.`, missed: [] };
  if (entered > truth) {
    return { entered, truth, verdict: 'over', line: `You said ${entered}; it's ${truth} — nothing scores the extra ${entered - truth}.`, missed: [] };
  }
  // Undercount: find a subset of the combinations worth EXACTLY the difference,
  // preferring the rarest kinds (nobs, flush, run) since those are what people
  // miss; fall back to a greedy pick if no exact subset exists.
  const diff = truth - entered;
  const order = ['nobs', 'flush', 'run', 'pair', 'fifteen'];
  const sorted = [...score.items].sort((a, b) => order.indexOf(a.kind) - order.indexOf(b.kind));
  const missed = exactSubset(sorted, diff) ?? greedy(sorted, diff);
  const names = missed.map((m) => itemLabel(m).toLowerCase() + (m.kind === 'fifteen' ? ` (${m.cards.map(label).join(' ')})` : m.kind === 'pair' ? ` of ${RANK_NAME[rankOf(m.cards[0]!)]}s` : ''));
  return { entered, truth, verdict: 'under', line: `You said ${entered}; it's ${truth} — missed: ${names.join(', ')}.`, missed };
}

function exactSubset(items: Score['items'], target: number): Score['items'] | null {
  // Depth-first over items in preference order; first exact match wins, which
  // keeps rare kinds (earlier in the order) in the answer when possible.
  const pick: Score['items'] = [];
  const rec = (i: number, remaining: number): boolean => {
    if (remaining === 0) return true;
    if (i >= items.length || remaining < 0) return false;
    pick.push(items[i]!);
    if (rec(i + 1, remaining - items[i]!.points)) return true;
    pick.pop();
    return rec(i + 1, remaining);
  };
  return rec(0, target) ? pick : null;
}

function greedy(items: Score['items'], target: number): Score['items'] {
  const out: Score['items'] = [];
  let acc = 0;
  for (const it of items) {
    if (acc >= target) break;
    out.push(it);
    acc += it.points;
  }
  return out;
}

/** For the aria-live region: "8 points. Fifteen 2, fifteen 4, pair 6, run 8." */
export function speakScore(score: Score): string {
  if (score.total === 0) return 'No points. Nineteen.';
  let running = 0;
  const parts: string[] = [];
  for (const it of score.items) {
    running += it.points;
    parts.push(`${itemLabel(it)} ${running}`);
  }
  return `${score.total} points. ${parts.join(', ')}.`;
}

export const cardValueWord = (c: Card) => (valueOf(c) === 10 && rankOf(c) > 10 ? 'ten-card' : String(valueOf(c)));
